import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import * as FiIcons from "react-icons/fi";
import SafeIcon from "../common/SafeIcon";

const { FiMic, FiMicOff, FiLoader, FiGlobe } = FiIcons;

const SUPPORTED_LANGUAGES = [
  { code: "th-TH", name: "ไทย" },
  { code: "en-US", name: "English" },
  { code: "zh-CN", name: "中文" },
  { code: "ja-JP", name: "日本語" },
];

const VoiceInput = ({ onVoiceInput }) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recognition, setRecognition] = useState(null);
  const [selectedLanguage, setSelectedLanguage] = useState("th-TH");
  const [error, setError] = useState(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  useEffect(() => {
    initializeSpeechRecognition();
    return () => {
      if (recognition) {
        recognition.stop();
      }
    };
  }, [selectedLanguage]);

  const initializeSpeechRecognition = () => {
    try {
      const SpeechRecognition =
        window.SpeechRecognition || window.webkitSpeechRecognition;
      if (SpeechRecognition) {
        const recognitionInstance = new SpeechRecognition();
        recognitionInstance.continuous = false;
        recognitionInstance.interimResults = true;
        recognitionInstance.lang = selectedLanguage;

        recognitionInstance.onstart = () => {
          setIsRecording(true);
          setError(null);
          setIsMenuOpen(false); // ปิดเมนูเมื่อเริ่มอัด
        };

        recognitionInstance.onend = () => {
          setIsRecording(false);
          setIsProcessing(false);
        };

        recognitionInstance.onresult = (event) => {
          const transcript = Array.from(event.results)
            .map((result) => result[0].transcript)
            .join("");

          if (event.results[0].isFinal) {
            onVoiceInput(transcript);
          }
        };

        recognitionInstance.onerror = (event) => {
          console.error("Speech recognition error:", event.error);
          setError(event.error);
          setIsRecording(false);
          setIsProcessing(false);
        };

        setRecognition(recognitionInstance);
      }
    } catch (e) {
      console.error("Speech recognition initialization error:", e);
      setError("Speech recognition not supported");
    }
  };

  const handleVoiceToggle = () => {
    if (isRecording) {
      recognition?.stop();
    } else {
      if (recognition) {
        setIsProcessing(true);
        setError(null);
        try {
          recognition.start();
        } catch (error) {
          console.error("Error starting voice input:", error);
          setError("Failed to start recording");
          setIsProcessing(false);
        }
      }
    }
  };

  const getButtonStyle = () => {
    if (isProcessing && !isRecording)
      return "bg-yellow-200 text-yellow-700 animate-pulse";
    if (isRecording) return "bg-red-500 text-white animate-pulse";
    if (error) return "bg-red-200 text-red-700";
    return "bg-slate-200 text-slate-600 hover:bg-slate-300";
  };

  return (
    <div className="flex items-center gap-2">
      <div className="relative">
        {/* 1. ปุ่มเลือกภาษา */}
        <button
          type="button"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          disabled={isRecording || isProcessing}
          className="p-2 text-slate-400 hover:text-slate-600 disabled:opacity-50 transition-colors"
          title="เปลี่ยนภาษา"
        >
          <FiGlobe className="w-5 h-5" />
        </button>
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              className="absolute bottom-full right-0 mb-2 w-32 bg-white rounded-lg shadow-lg border border-slate-200 z-10 p-1"
            >
              {SUPPORTED_LANGUAGES.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => {
                    setSelectedLanguage(lang.code);
                    setIsMenuOpen(false);
                  }}
                  className={`w-full text-left text-sm px-2 py-1.5 rounded-md ${
                    selectedLanguage === lang.code
                      ? "bg-green-100 text-green-800 font-semibold"
                      : "text-slate-700 hover:bg-slate-100"
                  }`}
                >
                  {lang.name}
                </button>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* 2. ปุ่มอัดเสียง */}
      <motion.button
        onClick={handleVoiceToggle}
        disabled={isProcessing && !isRecording}
        className={`w-8 h-8 flex items-center justify-center rounded-full transition-all duration-200 ${getButtonStyle()}`}
        whileTap={{ scale: 0.9 }}
        title={isRecording ? "หยุด" : "อัดเสียง"}
      >
        {isProcessing && !isRecording ? (
          <SafeIcon icon={FiLoader} className="w-5 h-5 animate-spin" />
        ) : isRecording ? (
          <SafeIcon icon={FiMicOff} className="w-5 h-5" />
        ) : (
          <SafeIcon icon={FiMic} className="w-5 h-5" />
        )}
      </motion.button>

      {/* 3. Error Message (ถ้ามี) */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: 5 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 5 }}
            className="absolute bottom-full left-0 mb-2 w-max p-1.5 px-2 bg-white text-red-600 text-xs font-semibold rounded-md shadow-lg border border-red-200"
          >
            {error === "not-allowed"
              ? "โปรดอนุญาตการใช้ไมโครโฟน"
              : "เกิดข้อผิดพลาด"}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default VoiceInput;
